/**
 * Elegir una imagen: de los archivos, o tomándola con la cámara.
 *
 * SON DOS `<input>` Y NO UNO, a propósito. Un solo campo con
 * `capture="user"` abre la cámara DIRECTO en el celular y esconde la galería;
 * sin `capture`, en muchos Android abre solo el explorador de archivos. Con
 * dos botones, la persona elige qué quiere hacer y las dos cosas funcionan en
 * los dos sistemas. En computadora el botón de cámara igual abre el
 * explorador —el navegador ignora `capture` si no hay cámara— así que no hay
 * nada que ocultar según el dispositivo, que además nunca se detecta bien.
 *
 * La comprobación de acá es de conveniencia, no de seguridad: sirve para
 * decirle a la persona en el acto que ese archivo no va, en vez de que suba
 * ocho megas y le rebote. Quien decide de verdad es el servidor, que mira los
 * bytes.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCEPTED_IMAGE_MIME_TYPES, IMAGE_MAX_UPLOAD_BYTES } from '@tennisup/contracts';

interface ImagePickerProps {
  id: string;
  label: string;
  help?: string;
  /** La imagen que ya está guardada, si hay. Se muestra hasta que se elija otra. */
  currentUrl?: string | null;
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

export function ImagePicker({
  id,
  label,
  help,
  currentUrl = null,
  value,
  onChange,
  disabled = false,
}: ImagePickerProps): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  // La URL de la vista previa hay que REVOCARLA. Cada `createObjectURL` deja
  // el archivo entero retenido en memoria hasta que se revoque; probando
  // cinco fotos seguidas, eso son cinco fotos que el navegador no puede
  // soltar.
  useEffect(() => {
    if (value === null) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  function accept(file: File | undefined): void {
    if (file === undefined) return;

    if (!ACCEPTED_IMAGE_MIME_TYPES.some((type) => file.type === type)) {
      setError(t('image.wrongType'));
      onChange(null);
      return;
    }
    if (file.size > IMAGE_MAX_UPLOAD_BYTES) {
      setError(t('image.tooBig', { megabytes: Math.floor(IMAGE_MAX_UPLOAD_BYTES / (1024 * 1024)) }));
      onChange(null);
      return;
    }

    setError(null);
    onChange(file);
  }

  function clear(): void {
    setError(null);
    onChange(null);
    if (fileInput.current !== null) fileInput.current.value = '';
    if (cameraInput.current !== null) cameraInput.current.value = '';
  }

  const shown = preview ?? currentUrl;

  return (
    <div className="field image-picker">
      <label htmlFor={id}>{label}</label>
      {help !== undefined && (
        <span className="help" id={`${id}-help`}>
          {help}
        </span>
      )}

      {shown !== null && (
        // Vacío a propósito: la imagen no aporta información que no esté ya
        // en la etiqueta del campo, y describirla sería inventar.
        <img className="image-preview" src={shown} alt="" />
      )}

      <div className="image-actions">
        <input
          ref={fileInput}
          id={id}
          type="file"
          accept={ACCEPTED_IMAGE_MIME_TYPES.join(',')}
          aria-describedby={help === undefined ? undefined : `${id}-help`}
          disabled={disabled}
          onChange={(e) => accept(e.target.files?.[0])}
        />

        <label className="camera-button" htmlFor={`${id}-camera`}>
          {t('image.takePhoto')}
        </label>
        <input
          ref={cameraInput}
          id={`${id}-camera`}
          className="visually-hidden"
          type="file"
          accept={ACCEPTED_IMAGE_MIME_TYPES.join(',')}
          capture="user"
          disabled={disabled}
          onChange={(e) => accept(e.target.files?.[0])}
        />

        {value !== null && (
          <button type="button" className="link" onClick={clear} disabled={disabled}>
            {t('image.clear')}
          </button>
        )}
      </div>

      {error !== null && (
        <span className="error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
